import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload, Package, Users, ShoppingCart, DollarSign, Image, FileText, Layers, Grid, FolderOpen, Film, Play, Rocket, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({ total_products: 0, total_orders: 0, total_customers: 0, total_revenue: 0 });
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // CMS State
  const [shopCategories, setShopCategories] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pageContent, setPageContent] = useState({});
  
  // Dialog State
  const [showShopCategoryDialog, setShowShopCategoryDialog] = useState(false);
  const [showShopProductDialog, setShowShopProductDialog] = useState(false);
  const [showGalleryCategoryDialog, setShowGalleryCategoryDialog] = useState(false);
  const [showGalleryItemDialog, setShowGalleryItemDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  
  // Editing State
  const [editingShopCategory, setEditingShopCategory] = useState(null);
  const [editingShopProduct, setEditingShopProduct] = useState(null);
  const [editingGalleryCategory, setEditingGalleryCategory] = useState(null);
  const [editingGalleryItem, setEditingGalleryItem] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  
  // Form State
  const [shopCategoryForm, setShopCategoryForm] = useState({ name: '', description: '', image: '', order: 0 });
  const [shopProductForm, setShopProductForm] = useState({ 
    category_id: '', name: '', description: '', base_price: '', diamond_price: '', image: '', in_stock: true,
    variants: [], lead_time: ''
  });
  const [galleryCategoryForm, setGalleryCategoryForm] = useState({ name: '', description: '', image: '', order: 0 });
  const [galleryItemForm, setGalleryItemForm] = useState({ category_id: '', title: '', description: '', image: '', images: ['', '', '', '', '', '', '', '', '', ''] });
  const [projectForm, setProjectForm] = useState({ 
    title: '', 
    description: '', 
    client: '', 
    thumbnail: '',
    featured: false,
    order: 0,
    stages: [
      { name: 'Beginning', description: '', images: ['', '', '', ''], video_url: '' },
      { name: 'In Progress', description: '', images: ['', '', '', ''], video_url: '' },
      { name: 'Final Product', description: '', images: ['', '', '', ''], video_url: '' }
    ]
  });
  
  // Page Content Forms
  const [heroContent, setHeroContent] = useState({ title: '', content: '', image: '' });
  const [whoWeAreContent, setWhoWeAreContent] = useState({ title: '', content: '' });
  const [contactContent, setContactContent] = useState({ extra_data: { address: '', phone: '', email: '' } });
  
  // Upload State
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [currentUploadTarget, setCurrentUploadTarget] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const getToken = () => localStorage.getItem('token');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchAll = async () => {
    fetchStats();
    fetchOrders();
    fetchCustomers();
    fetchShopCategories();
    fetchShopProducts();
    fetchGalleryCategories();
    fetchGalleryItems();
    fetchProjects();
    fetchPageContent();
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, authHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, authHeaders());
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, authHeaders());
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchShopCategories = async () => {
    try {
      const response = await axios.get(`${API}/shop-categories`);
      setShopCategories(response.data);
    } catch (error) {
      console.error('Error fetching shop categories:', error);
    }
  };

  const fetchShopProducts = async () => {
    try {
      const response = await axios.get(`${API}/shop-products`);
      setShopProducts(response.data);
    } catch (error) {
      console.error('Error fetching shop products:', error);
    }
  };

  const fetchGalleryCategories = async () => {
    try {
      const response = await axios.get(`${API}/gallery-categories`);
      setGalleryCategories(response.data);
    } catch (error) {
      console.error('Error fetching gallery categories:', error);
    }
  };

  const fetchGalleryItems = async () => {
    try {
      const response = await axios.get(`${API}/gallery-items`);
      setGalleryItems(response.data);
    } catch (error) {
      console.error('Error fetching gallery items:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchPageContent = async () => {
    try {
      const [hero, whoWeAre, contact] = await Promise.all([
        axios.get(`${API}/page-content/hero`),
        axios.get(`${API}/page-content/who_we_are`),
        axios.get(`${API}/page-content/contact`)
      ]);
      setHeroContent(hero.data);
      setWhoWeAreContent(whoWeAre.data);
      setContactContent(contact.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  // Image Upload Handler
  const handleImageUpload = async (e, callback, preset = 'gallery') => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'duraplast');
    formData.append('preset', preset);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formData, {
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      callback(response.data.url);
      toast.success(`Image uploaded & optimized! (${preset})`);
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  // Shop Category CRUD
  const handleSaveShopCategory = async () => {
    try {
      if (editingShopCategory) {
        await axios.put(`${API}/admin/shop-categories/${editingShopCategory.id}`, shopCategoryForm, authHeaders());
        toast.success('Category updated!');
      } else {
        await axios.post(`${API}/admin/shop-categories`, shopCategoryForm, authHeaders());
        toast.success('Category created!');
      }
      setShowShopCategoryDialog(false);
      setEditingShopCategory(null);
      setShopCategoryForm({ name: '', description: '', image: '', order: 0 });
      fetchShopCategories();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteShopCategory = async (id) => {
    if (!window.confirm('Delete this category and all its products?')) return;
    try {
      await axios.delete(`${API}/admin/shop-categories/${id}`, authHeaders());
      toast.success('Category deleted!');
      fetchShopCategories();
      fetchShopProducts();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Shop Product CRUD
  const handleSaveShopProduct = async () => {
    try {
      const data = { 
        ...shopProductForm, 
        base_price: parseFloat(shopProductForm.base_price),
        diamond_price: shopProductForm.diamond_price ? parseFloat(shopProductForm.diamond_price) : null
      };
      if (editingShopProduct) {
        await axios.put(`${API}/admin/shop-products/${editingShopProduct.id}`, data, authHeaders());
        toast.success('Product updated!');
      } else {
        await axios.post(`${API}/admin/shop-products`, data, authHeaders());
        toast.success('Product created!');
      }
      setShowShopProductDialog(false);
      setEditingShopProduct(null);
      setShopProductForm({ 
        category_id: '', name: '', description: '', base_price: '', diamond_price: '', image: '', in_stock: true,
        variants: [], lead_time: ''
      });
      fetchShopProducts();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteShopProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/shop-products/${id}`, authHeaders());
      toast.success('Product deleted!');
      fetchShopProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Gallery Category CRUD
  const handleSaveGalleryCategory = async () => {
    try {
      if (editingGalleryCategory) {
        await axios.put(`${API}/admin/gallery-categories/${editingGalleryCategory.id}`, galleryCategoryForm, authHeaders());
        toast.success('Category updated!');
      } else {
        await axios.post(`${API}/admin/gallery-categories`, galleryCategoryForm, authHeaders());
        toast.success('Category created!');
      }
      setShowGalleryCategoryDialog(false);
      setEditingGalleryCategory(null);
      setGalleryCategoryForm({ name: '', description: '', image: '', order: 0 });
      fetchGalleryCategories();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteGalleryCategory = async (id) => {
    if (!window.confirm('Delete this category and all its items?')) return;
    try {
      await axios.delete(`${API}/admin/gallery-categories/${id}`, authHeaders());
      toast.success('Category deleted!');
      fetchGalleryCategories();
      fetchGalleryItems();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Gallery Item CRUD
  const handleSaveGalleryItem = async () => {
    try {
      // Filter out empty images before saving
      const cleanedForm = {
        ...galleryItemForm,
        images: galleryItemForm.images?.filter(img => img && img.trim() !== '') || []
      };
      
      if (editingGalleryItem) {
        await axios.put(`${API}/admin/gallery-items/${editingGalleryItem.id}`, cleanedForm, authHeaders());
        toast.success('Item updated!');
      } else {
        await axios.post(`${API}/admin/gallery-items`, cleanedForm, authHeaders());
        toast.success('Item created!');
      }
      setShowGalleryItemDialog(false);
      setEditingGalleryItem(null);
      setGalleryItemForm({ category_id: '', title: '', description: '', image: '', images: ['', '', '', '', '', '', '', '', '', ''] });
      fetchGalleryItems();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDeleteGalleryItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/admin/gallery-items/${id}`, authHeaders());
      toast.success('Item deleted!');
      fetchGalleryItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Project CRUD
  const getDefaultProjectForm = () => ({
    title: '', 
    description: '', 
    client: '', 
    thumbnail: '',
    featured: false,
    order: 0,
    stages: [
      { name: 'Beginning', description: '', images: ['', '', '', ''], video_url: '' },
      { name: 'In Progress', description: '', images: ['', '', '', ''], video_url: '' },
      { name: 'Final Product', description: '', images: ['', '', '', ''], video_url: '' }
    ]
  });

  const handleSaveProject = async () => {
    try {
      // Clean up stages - filter out empty images
      const cleanedStages = projectForm.stages.map(stage => ({
        ...stage,
        images: stage.images?.filter(img => img && img.trim() !== '') || []
      }));
      
      const cleanedForm = {
        ...projectForm,
        stages: cleanedStages
      };
      
      if (editingProject) {
        await axios.put(`${API}/admin/projects/${editingProject.id}`, cleanedForm, authHeaders());
        toast.success('Project updated!');
      } else {
        await axios.post(`${API}/admin/projects`, cleanedForm, authHeaders());
        toast.success('Project created!');
      }
      setShowProjectDialog(false);
      setEditingProject(null);
      setProjectForm(getDefaultProjectForm());
      fetchProjects();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, authHeaders());
      toast.success('Project deleted!');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const updateStage = (stageIndex, field, value) => {
    const newStages = [...projectForm.stages];
    newStages[stageIndex] = { ...newStages[stageIndex], [field]: value };
    setProjectForm({ ...projectForm, stages: newStages });
  };

  const updateStageImage = (stageIndex, imageIndex, url) => {
    const newStages = [...projectForm.stages];
    const newImages = [...(newStages[stageIndex].images || ['', '', '', ''])];
    newImages[imageIndex] = url;
    newStages[stageIndex] = { ...newStages[stageIndex], images: newImages };
    setProjectForm({ ...projectForm, stages: newStages });
  };

  // Page Content Save
  const handleSavePageContent = async (pageKey, content) => {
    try {
      await axios.put(`${API}/admin/page-content/${pageKey}`, content, authHeaders());
      toast.success('Content saved!');
      fetchPageContent();
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  // Image Upload Component
  const ImageUploader = ({ value, onChange, label, preset = 'gallery' }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="flex-1" />
        <input
          type="file"
          id={`upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
          className="hidden"
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', 'duraplast');
            formData.append('preset', preset);
            try {
              const response = await axios.post(`${API}/admin/upload-image`, formData, {
                ...authHeaders(),
                headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
              });
              onChange(response.data.url);
              toast.success(`Image optimized for ${preset}!`);
            } catch (error) {
              toast.error('Failed to upload image');
            } finally {
              setUploading(false);
              e.target.value = '';
            }
          }}
        />
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => document.getElementById(`upload-${label.replace(/\s+/g, '-').toLowerCase()}`).click()}
          disabled={uploading}
        >
          <Upload size={16} className="mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
      {value && <img src={value} alt="Preview" className="w-32 h-32 object-cover rounded border" />}
    </div>
  );

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-screen">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={(e) => {
          if (currentUploadTarget) {
            handleImageUpload(e, currentUploadTarget);
          }
        }}
      />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <h1 className="text-4xl font-black mb-8">Admin Dashboard</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 border border-white/10 p-6">
            <Package className="text-[#D4AF37] mb-2" size={24} />
            <p className="text-2xl font-bold">{stats.total_products}</p>
            <p className="text-white/60 text-sm">Products</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-6">
            <ShoppingCart className="text-[#D4AF37] mb-2" size={24} />
            <p className="text-2xl font-bold">{stats.total_orders}</p>
            <p className="text-white/60 text-sm">Orders</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-6">
            <Users className="text-[#D4AF37] mb-2" size={24} />
            <p className="text-2xl font-bold">{stats.total_customers}</p>
            <p className="text-white/60 text-sm">Customers</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-6">
            <DollarSign className="text-[#D4AF37] mb-2" size={24} />
            <p className="text-2xl font-bold">R{stats.total_revenue?.toFixed(2) || '0.00'}</p>
            <p className="text-white/60 text-sm">Revenue</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="shop-categories" className="w-full">
          <TabsList className="grid grid-cols-4 md:grid-cols-8 gap-1 mb-6 h-auto bg-white/5 p-1">
            <TabsTrigger value="shop-categories" className="text-xs py-2"><Layers size={14} className="mr-1" />Shop Categories</TabsTrigger>
            <TabsTrigger value="shop-products" className="text-xs py-2"><Package size={14} className="mr-1" />Shop Products</TabsTrigger>
            <TabsTrigger value="gallery-categories" className="text-xs py-2"><FolderOpen size={14} className="mr-1" />Gallery Categories</TabsTrigger>
            <TabsTrigger value="gallery-items" className="text-xs py-2"><Image size={14} className="mr-1" />Gallery Items</TabsTrigger>
            <TabsTrigger value="projects" className="text-xs py-2"><Film size={14} className="mr-1" />Projects</TabsTrigger>
            <TabsTrigger value="page-content" className="text-xs py-2"><FileText size={14} className="mr-1" />Page Content</TabsTrigger>
            <TabsTrigger value="orders" className="text-xs py-2"><ShoppingCart size={14} className="mr-1" />Orders</TabsTrigger>
            <TabsTrigger value="customers" className="text-xs py-2"><Users size={14} className="mr-1" />Customers</TabsTrigger>
          </TabsList>

          {/* Shop Categories Tab */}
          <TabsContent value="shop-categories">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Shop Categories</h2>
              <Button onClick={() => { setEditingShopCategory(null); setShopCategoryForm({ name: '', description: '', image: '', order: 0 }); setShowShopCategoryDialog(true); }}>
                <Plus size={16} className="mr-1" /> Add Category
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {shopCategories.map((cat) => (
                <div key={cat.id} className="bg-white/5 border border-white/10 p-4 rounded">
                  {cat.image && <img src={cat.image} alt={cat.name} className="w-full h-32 object-cover rounded mb-3" />}
                  <h3 className="font-bold">{cat.name}</h3>
                  <p className="text-sm text-white/60 mb-3">{cat.description}</p>
                  <p className="text-xs text-white/40 mb-3">Order: {cat.order}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditingShopCategory(cat); setShopCategoryForm(cat); setShowShopCategoryDialog(true); }}>
                      <Edit size={14} />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteShopCategory(cat.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
              {shopCategories.length === 0 && <p className="text-white/50 col-span-3 text-center py-8">No categories yet. Add your first category!</p>}
            </div>
          </TabsContent>

          {/* Shop Products Tab */}
          <TabsContent value="shop-products">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Shop Products</h2>
              <Button onClick={() => { 
                setEditingShopProduct(null); 
                setShopProductForm({ 
                  category_id: shopCategories[0]?.id || '', name: '', description: '', base_price: '', diamond_price: '', image: '', in_stock: true,
                  variants: [], lead_time: ''
                }); 
                setShowShopProductDialog(true); 
              }} disabled={shopCategories.length === 0}>
                <Plus size={16} className="mr-1" /> Add Product
              </Button>
            </div>
            {shopCategories.length === 0 && <p className="text-white/50 text-center py-8">Create a shop category first before adding products.</p>}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {shopProducts.map((prod) => (
                <div key={prod.id} className="bg-white/5 border border-white/10 p-4 rounded">
                  {prod.image && <img src={prod.image} alt={prod.name} className="w-full h-32 object-cover rounded mb-3" />}
                  <h3 className="font-bold">{prod.name}</h3>
                  <p className="text-sm text-white/60 mb-1">{prod.description?.substring(0, 50)}...</p>
                  <p className="text-[#D4AF37] font-bold mb-1">R{prod.base_price?.toFixed(2)}</p>
                  <p className="text-xs text-white/40 mb-3">{prod.variants?.length || 0} variants</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { 
                      setEditingShopProduct(prod); 
                      setShopProductForm({...prod, variants: (prod.variants || []).map(v => ({...v}))}); 
                      setShowShopProductDialog(true); 
                    }}>
                      <Edit size={14} />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteShopProduct(prod.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Gallery Categories Tab */}
          <TabsContent value="gallery-categories">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Gallery Categories</h2>
              <Button onClick={() => { setEditingGalleryCategory(null); setGalleryCategoryForm({ name: '', description: '', image: '', order: 0 }); setShowGalleryCategoryDialog(true); }}>
                <Plus size={16} className="mr-1" /> Add Category
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {galleryCategories.map((cat) => (
                <div key={cat.id} className="bg-white/5 border border-white/10 p-4 rounded">
                  {cat.image && <img src={cat.image} alt={cat.name} className="w-full h-32 object-cover rounded mb-3" />}
                  <h3 className="font-bold">{cat.name}</h3>
                  <p className="text-sm text-white/60 mb-3">{cat.description}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditingGalleryCategory(cat); setGalleryCategoryForm(cat); setShowGalleryCategoryDialog(true); }}>
                      <Edit size={14} />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteGalleryCategory(cat.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
              {galleryCategories.length === 0 && <p className="text-white/50 col-span-3 text-center py-8">No categories yet. Add your first gallery category!</p>}
            </div>
          </TabsContent>

          {/* Gallery Items Tab */}
          <TabsContent value="gallery-items">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Gallery Items</h2>
              <Button onClick={() => { 
                setEditingGalleryItem(null); 
                setGalleryItemForm({ category_id: galleryCategories[0]?.id || '', title: '', description: '', image: '', images: ['', '', '', '', '', '', '', '', '', ''] }); 
                setShowGalleryItemDialog(true); 
              }} disabled={galleryCategories.length === 0}>
                <Plus size={16} className="mr-1" /> Add Item
              </Button>
            </div>
            {galleryCategories.length === 0 && <p className="text-white/50 text-center py-8">Create a gallery category first before adding items.</p>}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {galleryItems.map((item) => {
                const itemImages = item.images?.filter(img => img && img.trim() !== '') || [];
                const displayImage = itemImages[0] || item.image;
                return (
                  <div key={item.id} className="bg-white/5 border border-white/10 p-4 rounded">
                    {displayImage && <img src={displayImage} alt={item.title} className="w-full h-32 object-cover rounded mb-3" />}
                    <h3 className="font-bold">{item.title}</h3>
                    <p className="text-sm text-white/60 mb-1">{item.description?.substring(0, 50)}...</p>
                    {itemImages.length > 0 && <p className="text-xs text-[#D4AF37] mb-3">{itemImages.length} image{itemImages.length !== 1 ? 's' : ''}</p>}
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { 
                        setEditingGalleryItem(item); 
                        // Ensure images array has 10 slots when editing
                        const existingImages = item.images || [];
                        const paddedImages = [...existingImages, ...Array(10 - existingImages.length).fill('')].slice(0, 10);
                        setGalleryItemForm({...item, images: paddedImages}); 
                        setShowGalleryItemDialog(true); 
                      }}>
                        <Edit size={14} />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteGalleryItem(item.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Projects</h2>
              <Button onClick={() => { 
                setEditingProject(null); 
                setProjectForm(getDefaultProjectForm()); 
                setShowProjectDialog(true); 
              }}>
                <Plus size={16} className="mr-1" /> Add Project
              </Button>
            </div>
            <p className="text-white/60 mb-6">Showcase your manufacturing journey from concept to completion with images and videos for each stage.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projects.map((project) => {
                const totalMedia = project.stages?.reduce((acc, stage) => 
                  acc + (stage.images?.filter(img => img)?.length || 0) + (stage.video_url ? 1 : 0), 0) || 0;
                return (
                  <div key={project.id} className="bg-white/5 border border-white/10 p-4 rounded">
                    <div className="flex gap-4">
                      {project.thumbnail ? (
                        <img src={project.thumbnail} alt={project.title} className="w-32 h-24 object-cover rounded" />
                      ) : (
                        <div className="w-32 h-24 bg-black/40 rounded flex items-center justify-center">
                          <Film size={24} className="text-white/20" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold">{project.title}</h3>
                            {project.client && <p className="text-xs text-[#D4AF37]">Client: {project.client}</p>}
                          </div>
                          {project.featured && (
                            <span className="bg-[#D4AF37] text-black px-2 py-0.5 text-xs font-bold rounded">FEATURED</span>
                          )}
                        </div>
                        <p className="text-sm text-white/60 mt-1">{project.description?.substring(0, 80)}...</p>
                        <p className="text-xs text-white/40 mt-2">{totalMedia} media files • {project.stages?.length || 0} stages</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => { 
                        setEditingProject(project); 
                        // Ensure stages have proper structure with 4 image slots
                        const formStages = (project.stages?.length > 0 ? project.stages : [
                          { name: 'Beginning', description: '', images: [], video_url: '' },
                          { name: 'In Progress', description: '', images: [], video_url: '' },
                          { name: 'Final Product', description: '', images: [], video_url: '' }
                        ]).map(stage => ({
                          ...stage,
                          images: [...(stage.images || []), '', '', '', ''].slice(0, 4)
                        }));
                        setProjectForm({...project, stages: formStages}); 
                        setShowProjectDialog(true); 
                      }}>
                        <Edit size={14} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteProject(project.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Page Content Tab */}
          <TabsContent value="page-content">
            <h2 className="text-2xl font-bold mb-6">Page Content Editor</h2>
            
            {/* Hero Section */}
            <div className="bg-white/5 border border-white/10 p-6 rounded mb-6">
              <h3 className="text-xl font-bold mb-4">Hero Section (Landing Page)</h3>
              <div className="space-y-4">
                <div>
                  <Label>Hero Title</Label>
                  <Input value={heroContent.title || ''} onChange={(e) => setHeroContent({...heroContent, title: e.target.value})} placeholder="e.g., MANUFACTURING EXCELLENCE" />
                </div>
                <div>
                  <Label>Hero Subtitle</Label>
                  <Textarea value={heroContent.content || ''} onChange={(e) => setHeroContent({...heroContent, content: e.target.value})} placeholder="Hero description text..." />
                </div>
                <ImageUploader label="Hero Background Image" preset="hero" value={heroContent.image || ''} onChange={(url) => setHeroContent({...heroContent, image: url})} />
                <Button onClick={() => handleSavePageContent('hero', heroContent)}>Save Hero Content</Button>
              </div>
            </div>

            {/* Who We Are Section */}
            <div className="bg-white/5 border border-white/10 p-6 rounded mb-6">
              <h3 className="text-xl font-bold mb-4">Who We Are Section</h3>
              <div className="space-y-4">
                <div>
                  <Label>Section Title</Label>
                  <Input value={whoWeAreContent.title || ''} onChange={(e) => setWhoWeAreContent({...whoWeAreContent, title: e.target.value})} placeholder="WHO WE ARE" />
                </div>
                <div>
                  <Label>Content (supports multiple paragraphs, separate with double line breaks)</Label>
                  <Textarea 
                    value={whoWeAreContent.content || ''} 
                    onChange={(e) => setWhoWeAreContent({...whoWeAreContent, content: e.target.value})} 
                    placeholder="Enter your company story here..."
                    className="min-h-[300px]"
                  />
                </div>
                <Button onClick={() => handleSavePageContent('who_we_are', whoWeAreContent)}>Save Who We Are Content</Button>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-white/5 border border-white/10 p-6 rounded">
              <h3 className="text-xl font-bold mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div>
                  <Label>Address</Label>
                  <Input 
                    value={contactContent.extra_data?.address || ''} 
                    onChange={(e) => setContactContent({...contactContent, extra_data: {...contactContent.extra_data, address: e.target.value}})} 
                    placeholder="Business address" 
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={contactContent.extra_data?.phone || ''} 
                    onChange={(e) => setContactContent({...contactContent, extra_data: {...contactContent.extra_data, phone: e.target.value}})} 
                    placeholder="Phone number" 
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    value={contactContent.extra_data?.email || ''} 
                    onChange={(e) => setContactContent({...contactContent, extra_data: {...contactContent.extra_data, email: e.target.value}})} 
                    placeholder="Email address" 
                  />
                </div>
                <Button onClick={() => handleSavePageContent('contact', contactContent)}>Save Contact Info</Button>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <h2 className="text-2xl font-bold mb-4">Orders</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3">Order ID</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} className="border-b border-white/5">
                      <td className="p-3 font-mono text-xs">{order.id.substring(0, 8)}...</td>
                      <td className="p-3">{order.guest_name || order.guest_email || 'Registered User'}</td>
                      <td className="p-3 text-[#D4AF37]">R{order.total?.toFixed(2)}</td>
                      <td className="p-3"><span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">{order.status}</span></td>
                      <td className="p-3 text-white/60">{new Date(order.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length === 0 && <p className="text-white/50 text-center py-8">No orders yet.</p>}
            </div>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <h2 className="text-2xl font-bold mb-4">Customers</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3">Company</th>
                    <th className="text-left p-3">Contact Person</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className="border-b border-white/5">
                      <td className="p-3 font-bold">{customer.company_name}</td>
                      <td className="p-3">{customer.contact_person}</td>
                      <td className="p-3 text-white/60">{customer.email}</td>
                      <td className="p-3 text-white/60">{customer.contact_phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {customers.length === 0 && <p className="text-white/50 text-center py-8">No customers yet.</p>}
            </div>
          </TabsContent>
        </Tabs>

        {/* Shop Category Dialog */}
        <Dialog open={showShopCategoryDialog} onOpenChange={setShowShopCategoryDialog}>
          <DialogContent className="bg-[#0a0a0a] border-white/10">
            <DialogHeader>
              <DialogTitle>{editingShopCategory ? 'Edit' : 'Add'} Shop Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={shopCategoryForm.name} onChange={(e) => setShopCategoryForm({...shopCategoryForm, name: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={shopCategoryForm.description} onChange={(e) => setShopCategoryForm({...shopCategoryForm, description: e.target.value})} /></div>
              <ImageUploader label="Category Image" preset="thumbnail" value={shopCategoryForm.image} onChange={(url) => setShopCategoryForm({...shopCategoryForm, image: url})} />
              <div><Label>Display Order</Label><Input type="number" value={shopCategoryForm.order} onChange={(e) => setShopCategoryForm({...shopCategoryForm, order: parseInt(e.target.value) || 0})} /></div>
              <Button onClick={handleSaveShopCategory} className="w-full">Save Category</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Shop Product Dialog */}
        <Dialog open={showShopProductDialog} onOpenChange={setShowShopProductDialog}>
          <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingShopProduct ? 'Edit' : 'Add'} Shop Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={shopProductForm.category_id} onValueChange={(val) => setShopProductForm({...shopProductForm, category_id: val})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {shopCategories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Name</Label><Input value={shopProductForm.name} onChange={(e) => setShopProductForm({...shopProductForm, name: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={shopProductForm.description} onChange={(e) => setShopProductForm({...shopProductForm, description: e.target.value})} /></div>
              
              {/* Pricing Section */}
              <div className="border border-white/10 p-4 rounded space-y-3">
                <Label className="text-[#D4AF37] block">Pricing</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Regular Price (R)</Label>
                    <Input type="number" value={shopProductForm.base_price} onChange={(e) => setShopProductForm({...shopProductForm, base_price: e.target.value})} placeholder="e.g. 1000" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Diamond Member Price (R)</Label>
                    <Input type="number" value={shopProductForm.diamond_price || ''} onChange={(e) => setShopProductForm({...shopProductForm, diamond_price: e.target.value})} placeholder="Leave empty for 10% off" />
                    <p className="text-xs text-white/40 mt-1">If empty, Diamond members get 10% off regular price</p>
                  </div>
                </div>
              </div>
              
              <ImageUploader label="Product Image" preset="product" value={shopProductForm.image} onChange={(url) => setShopProductForm({...shopProductForm, image: url})} />
              
              <div>
                <Label className="mb-2 block">Variants (Size Options) - Optional</Label>
                {shopProductForm.variants?.map((variant, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-2">
                    <Input placeholder="Name" value={variant.name} onChange={(e) => {
                      const newVariants = shopProductForm.variants.map((v, i) => 
                        i === idx ? {...v, name: e.target.value} : v
                      );
                      setShopProductForm({...shopProductForm, variants: newVariants});
                    }} />
                    <Input placeholder="Dimensions" value={variant.dimensions} onChange={(e) => {
                      const newVariants = shopProductForm.variants.map((v, i) => 
                        i === idx ? {...v, dimensions: e.target.value} : v
                      );
                      setShopProductForm({...shopProductForm, variants: newVariants});
                    }} />
                    <Input type="number" placeholder="Price Add" value={variant.price_add} onChange={(e) => {
                      const newVariants = shopProductForm.variants.map((v, i) => 
                        i === idx ? {...v, price_add: parseFloat(e.target.value) || 0} : v
                      );
                      setShopProductForm({...shopProductForm, variants: newVariants});
                    }} />
                    <Button type="button" variant="destructive" size="sm" onClick={() => {
                      const newVariants = shopProductForm.variants.filter((_, i) => i !== idx);
                      setShopProductForm({...shopProductForm, variants: newVariants});
                    }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
                {(!shopProductForm.variants || shopProductForm.variants.length === 0) && (
                  <p className="text-white/40 text-sm mb-2">No variants added. Products without variants will be sold as single items.</p>
                )}
                <Button type="button" variant="outline" size="sm" onClick={() => setShopProductForm({...shopProductForm, variants: [...(shopProductForm.variants || []), { name: '', dimensions: '', price_add: 0 }]})}>
                  <Plus size={14} className="mr-1" /> Add Variant
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={shopProductForm.in_stock} onChange={(e) => setShopProductForm({...shopProductForm, in_stock: e.target.checked})} />
                <Label>In Stock</Label>
              </div>

              {/* Lead Time - shown when not in stock */}
              {!shopProductForm.in_stock && (
                <div>
                  <Label className="mb-2 block">Lead Time (shown instead of "Out of Stock")</Label>
                  <Input 
                    value={shopProductForm.lead_time || ''} 
                    onChange={(e) => setShopProductForm({...shopProductForm, lead_time: e.target.value})} 
                    placeholder="e.g., 2-3 weeks, Made to order, 5 business days"
                  />
                  <p className="text-xs text-white/40 mt-1">Leave empty to show "Out of Stock"</p>
                </div>
              )}

              <Button onClick={handleSaveShopProduct} className="w-full">Save Product</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Gallery Category Dialog */}
        <Dialog open={showGalleryCategoryDialog} onOpenChange={setShowGalleryCategoryDialog}>
          <DialogContent className="bg-[#0a0a0a] border-white/10">
            <DialogHeader>
              <DialogTitle>{editingGalleryCategory ? 'Edit' : 'Add'} Gallery Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={galleryCategoryForm.name} onChange={(e) => setGalleryCategoryForm({...galleryCategoryForm, name: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={galleryCategoryForm.description} onChange={(e) => setGalleryCategoryForm({...galleryCategoryForm, description: e.target.value})} /></div>
              <ImageUploader label="Category Image" preset="thumbnail" value={galleryCategoryForm.image} onChange={(url) => setGalleryCategoryForm({...galleryCategoryForm, image: url})} />
              <div><Label>Display Order</Label><Input type="number" value={galleryCategoryForm.order} onChange={(e) => setGalleryCategoryForm({...galleryCategoryForm, order: parseInt(e.target.value) || 0})} /></div>
              <Button onClick={handleSaveGalleryCategory} className="w-full">Save Category</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Gallery Item Dialog */}
        <Dialog open={showGalleryItemDialog} onOpenChange={setShowGalleryItemDialog}>
          <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingGalleryItem ? 'Edit' : 'Add'} Gallery Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={galleryItemForm.category_id} onValueChange={(val) => setGalleryItemForm({...galleryItemForm, category_id: val})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {galleryCategories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input value={galleryItemForm.title} onChange={(e) => setGalleryItemForm({...galleryItemForm, title: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={galleryItemForm.description} onChange={(e) => setGalleryItemForm({...galleryItemForm, description: e.target.value})} /></div>
              
              {/* Multiple Images - Carousel */}
              <div className="border border-white/10 p-4 rounded">
                <Label className="text-[#D4AF37] mb-3 block">Carousel Images (up to 10)</Label>
                <p className="text-white/50 text-xs mb-4">Click the upload button to add images. Images will auto-rotate every 4 seconds.</p>
                <div className="grid grid-cols-2 gap-3">
                  {galleryItemForm.images?.map((img, idx) => (
                    <div key={idx} className="space-y-1">
                      <Label className="text-xs text-white/60">Image {idx + 1}</Label>
                      <div className="flex gap-1">
                        <Input 
                          value={img || ''} 
                          onChange={(e) => {
                            const newImages = [...(galleryItemForm.images || [])];
                            newImages[idx] = e.target.value;
                            setGalleryItemForm({...galleryItemForm, images: newImages});
                          }} 
                          placeholder="Image URL or upload"
                          className="text-xs"
                        />
                        <input
                          type="file"
                          id={`gallery-image-upload-${idx}`}
                          className="hidden"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setUploading(true);
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('folder', 'duraplast/gallery');
                            formData.append('preset', 'gallery');
                            try {
                              const response = await axios.post(`${API}/admin/upload-image`, formData, {
                                ...authHeaders(),
                                headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
                              });
                              const newImages = [...(galleryItemForm.images || [])];
                              newImages[idx] = response.data.url;
                              setGalleryItemForm(prev => ({...prev, images: newImages}));
                              toast.success('Image optimized for gallery!');
                            } catch (error) {
                              toast.error('Failed to upload image');
                            } finally {
                              setUploading(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => document.getElementById(`gallery-image-upload-${idx}`).click()}
                          disabled={uploading}
                          className="px-2"
                        >
                          <Upload size={14} />
                        </Button>
                      </div>
                      {img && <img src={img} alt="" className="w-full h-16 object-contain rounded border border-white/10" />}
                    </div>
                  ))}
                </div>
              </div>
              
              <Button onClick={handleSaveGalleryItem} className="w-full">Save Item</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Project Dialog */}
        <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Project Title</Label>
                  <Input value={projectForm.title} onChange={(e) => setProjectForm({...projectForm, title: e.target.value})} placeholder="e.g., Custom Lightbox Installation" />
                </div>
                <div>
                  <Label>Client Name</Label>
                  <Input value={projectForm.client} onChange={(e) => setProjectForm({...projectForm, client: e.target.value})} placeholder="e.g., Red Bull SA" />
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea value={projectForm.description} onChange={(e) => setProjectForm({...projectForm, description: e.target.value})} placeholder="Brief description of the project" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Thumbnail Image</Label>
                  <div className="flex gap-2">
                    <Input value={projectForm.thumbnail} onChange={(e) => setProjectForm({...projectForm, thumbnail: e.target.value})} placeholder="Thumbnail URL" className="flex-1" />
                    <input
                      type="file"
                      id="project-thumbnail-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setUploading(true);
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('folder', 'duraplast/projects');
                        formData.append('preset', 'thumbnail');
                        try {
                          const response = await axios.post(`${API}/admin/upload-image`, formData, {
                            ...authHeaders(),
                            headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
                          });
                          setProjectForm(prev => ({...prev, thumbnail: response.data.url}));
                          toast.success('Thumbnail optimized!');
                        } catch (error) {
                          toast.error('Failed to upload');
                        } finally {
                          setUploading(false);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button type="button" variant="outline" onClick={() => document.getElementById('project-thumbnail-upload').click()} disabled={uploading}>
                      <Upload size={14} />
                    </Button>
                  </div>
                  {projectForm.thumbnail && <img src={projectForm.thumbnail} alt="" className="w-full h-24 object-cover rounded mt-2" />}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="featured" checked={projectForm.featured} onChange={(e) => setProjectForm({...projectForm, featured: e.target.checked})} />
                    <Label htmlFor="featured">Featured Project</Label>
                  </div>
                  <div>
                    <Label>Display Order</Label>
                    <Input type="number" value={projectForm.order} onChange={(e) => setProjectForm({...projectForm, order: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
              </div>

              {/* Project Stages */}
              <div className="border border-white/10 p-4 rounded space-y-4">
                <Label className="text-[#D4AF37] text-lg block">Project Stages</Label>
                <p className="text-white/50 text-xs">Add images and videos for each stage of the project journey.</p>
                
                {projectForm.stages?.map((stage, stageIdx) => (
                  <div key={stageIdx} className="bg-white/5 border border-white/10 p-4 rounded">
                    <div className="flex items-center gap-2 mb-3">
                      {stageIdx === 0 && <Rocket size={18} className="text-[#D4AF37]" />}
                      {stageIdx === 1 && <Clock size={18} className="text-[#D4AF37]" />}
                      {stageIdx === 2 && <CheckCircle size={18} className="text-[#D4AF37]" />}
                      <h4 className="font-bold text-[#D4AF37]">{stage.name}</h4>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Stage Description</Label>
                        <Input 
                          value={stage.description} 
                          onChange={(e) => updateStage(stageIdx, 'description', e.target.value)} 
                          placeholder="Brief description of this stage"
                          className="text-sm"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs">Video URL (YouTube, Vimeo, or direct link)</Label>
                        <Input 
                          value={stage.video_url} 
                          onChange={(e) => updateStage(stageIdx, 'video_url', e.target.value)} 
                          placeholder="https://..."
                          className="text-sm"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs">Images (up to 4)</Label>
                        <div className="grid grid-cols-4 gap-2 mt-1">
                          {(stage.images || ['', '', '', '']).slice(0, 4).map((img, imgIdx) => (
                            <div key={imgIdx} className="space-y-1">
                              <div className="flex gap-1">
                                <Input 
                                  value={img || ''} 
                                  onChange={(e) => updateStageImage(stageIdx, imgIdx, e.target.value)} 
                                  placeholder="URL"
                                  className="text-xs"
                                />
                                <input
                                  type="file"
                                  id={`project-stage-${stageIdx}-image-${imgIdx}`}
                                  className="hidden"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    setUploading(true);
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    formData.append('folder', 'duraplast/projects');
                                    formData.append('preset', 'project');
                                    try {
                                      const response = await axios.post(`${API}/admin/upload-image`, formData, {
                                        ...authHeaders(),
                                        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
                                      });
                                      updateStageImage(stageIdx, imgIdx, response.data.url);
                                      toast.success('Image optimized for projects!');
                                    } catch (error) {
                                      toast.error('Failed to upload');
                                    } finally {
                                      setUploading(false);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => document.getElementById(`project-stage-${stageIdx}-image-${imgIdx}`).click()}
                                  disabled={uploading}
                                  className="px-2"
                                >
                                  <Upload size={12} />
                                </Button>
                              </div>
                              {img && <img src={img} alt="" className="w-full h-12 object-cover rounded" />}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button onClick={handleSaveProject} className="w-full">Save Project</Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
